/**
 * Pure Event type filter groups + the actor/type/search filter predicate shared by
 * ExperienceLog's history rows and pending-schedule rows. Kept separate from index.tsx
 * (which pulls in a lot of UI/service weight) so this logic is testable on its own.
 */
import { ExperienceEventType } from '@dadohr/shared/domainTypes/experience';
import { flatten } from 'lodash';
import { matchesExclusiveAllFilter } from '../../../../components/ChipFilterWithDropdown';
import type { LogItem } from '../../../../components/LogTable';
import { toSearchTermRegexp } from '../../../../services/user';

export type EventTypeFilterType =
  | 'SKIPPED_AND_UNSKIPPED'
  | 'MOVED'
  | 'UNCOMPLETED'
  | 'CHANGED_ANCHOR'
  | 'CHANGED_SUPPORTING_ACTOR'
  | 'ARCHIVED'
  | 'STARTED'
  | 'REMINDER'
  | 'OTHER';

// Other will be handled differently and just express everything else
export const EVENT_TYPE_FILTER_GROUPS: {
  [key in Exclude<EventTypeFilterType, 'OTHER'>]: {
    types: ExperienceEventType[];
  };
} = {
  SKIPPED_AND_UNSKIPPED: {
    types: [
      ExperienceEventType.MESSAGE_SKIPPED,
      ExperienceEventType.MESSAGE_UNSKIPPED,
      ExperienceEventType.TASK_SKIPPED,
      ExperienceEventType.TASK_UNSKIPPED,
      ExperienceEventType.SUB_TASK_SKIPPED,
      ExperienceEventType.SUB_TASK_UNSKIPPED,
      ExperienceEventType.AUTOMATION_SKIPPED,
      ExperienceEventType.AUTOMATION_UNSKIPPED,
      ExperienceEventType.AUTOMATION_ACTION_SKIPPED,
      ExperienceEventType.AUTOMATION_ACTION_UNSKIPPED
    ]
  },
  MOVED: {
    types: [
      ExperienceEventType.MESSAGE_MOVED,
      ExperienceEventType.TASK_MOVED,
      ExperienceEventType.AUTOMATION_MOVED
    ]
  },
  UNCOMPLETED: {
    types: [
      ExperienceEventType.TASK_UNDONE,
      ExperienceEventType.SUB_TASK_UNDONE
    ]
  },
  CHANGED_ANCHOR: { types: [ExperienceEventType.ANCHOR_CHANGED] },
  CHANGED_SUPPORTING_ACTOR: {
    types: [ExperienceEventType.SUPPORTING_ACTOR_CHANGED]
  },
  ARCHIVED: { types: [ExperienceEventType.EXP_ARCHIVED] },
  STARTED: {
    types: [
      ExperienceEventType.EXP_START,
      ExperienceEventType.TASK_START,
      ExperienceEventType.EXP_MESSAGE,
      ExperienceEventType.AUTOMATION_RAN
    ]
  },
  // Doesn't differentiate pre-deadline/on-deadline/overdue reminders — those are one
  // filter bucket, even though the row text itself does distinguish them.
  REMINDER: {
    types: [
      ExperienceEventType.TASK_REMINDER,
      ExperienceEventType.TASK_LATE_REMINDER,
      ExperienceEventType.TASK_LATE_REMINDER_MANUAL
    ]
  }
};

export const DEFINED_EVENT_TYPE_FILTERS = flatten(
  Object.values(EVENT_TYPE_FILTER_GROUPS).map((x) => x.types)
);

export const ALL_EVENT_TYPE_FILTERS: EventTypeFilterType[] = [
  ...(Object.keys(EVENT_TYPE_FILTER_GROUPS) as EventTypeFilterType[]),
  'OTHER' as const
];

export type LogFilters = {
  q: string;
  actorKeys: (string | 'AUTOMATION' | 'OTHER')[];
  types: EventTypeFilterType[];
};

/** Shared by history rows and pending-schedule rows, so both filter identically. */
export const applyLogFilters = (
  items: LogItem[] | null | undefined,
  filters: LogFilters
): LogItem[] | null | undefined => {
  if (!items) {
    return items;
  }
  let fi = items;
  if (filters.q) {
    const re = toSearchTermRegexp(filters.q);
    fi = fi.filter((l) => l.searchValue.match(re));
  }
  if (filters.actorKeys.length) {
    fi = fi.filter((l) => l.actor && matchesExclusiveAllFilter(filters.actorKeys, l.actor));
  }
  if (filters.types.length && filters.types.length !== ALL_EVENT_TYPE_FILTERS.length) {
    const selectedTypes = flatten(
      filters.types.map((f) => (f === 'OTHER' ? [] : EVENT_TYPE_FILTER_GROUPS[f]?.types || []))
    );
    const hasOther = filters.types.includes('OTHER');
    fi = fi.filter(
      (l) =>
        selectedTypes.includes(l.type) ||
        (hasOther && !DEFINED_EVENT_TYPE_FILTERS.includes(l.type))
    );
  }
  return fi;
};
